import { useId } from 'react';
import { Box, Link, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import type { ScanRunSummary } from '../../services/testService';

export type RunSourceData = NonNullable<ScanRunSummary['source']>;

/** A git SHA as people read it: the first seven characters. */
export const shortSha = (sha: string) => sha.slice(0, 7);

interface RunSourceProps {
  source: ScanRunSummary['source'];
}

/**
 * REV-813: where a run ingested from CI came from — commit, branch, pull
 * request and the CI run — as the API returned it (revenant-api
 * src/utils/ciSource.js).
 *
 * Only what the run has is shown: no empty rows and no placeholder dashes, and
 * nothing at all for a run with no CI provenance (a platform scan). A value is
 * a link only when the API sent its URL, which it does only for a GitHub
 * repository tracked place and only for values it has checked, so nothing a CI
 * caller wrote reaches an href here unchecked.
 */
export function RunSource({ source }: RunSourceProps) {
  const { t } = useTranslation();
  const headingId = useId();
  if (!source) return null;

  const rows: { key: string; term: string; value: React.ReactNode }[] = [];
  if (source.commitSha) {
    const sha = shortSha(source.commitSha);
    rows.push({
      key: 'commit',
      term: t('runSource.commit', 'Commit'),
      value: source.commitUrl ? (
        <Link href={source.commitUrl} sx={{ fontFamily: 'monospace' }}>
          {sha}
        </Link>
      ) : (
        <Box component="span" sx={{ fontFamily: 'monospace' }}>
          {sha}
        </Box>
      ),
    });
  }
  if (source.branch) {
    rows.push({ key: 'branch', term: t('runSource.branch', 'Branch'), value: source.branch });
  }
  if (source.pullRequest) {
    const label = `#${source.pullRequest.number}`;
    rows.push({
      key: 'pullRequest',
      term: t('runSource.pullRequest', 'Pull request'),
      value: source.pullRequest.url ? <Link href={source.pullRequest.url}>{label}</Link> : label,
    });
  }
  if (source.buildUrl) {
    rows.push({
      key: 'build',
      term: t('runSource.build', 'CI run'),
      value: <Link href={source.buildUrl}>{t('runSource.buildLink', 'GitHub Actions run')}</Link>,
    });
  }
  if (rows.length === 0) return null;

  return (
    <Box component="section" aria-labelledby={headingId} sx={{ mt: 2 }}>
      <Typography id={headingId} component="h2" variant="subtitle1" fontWeight={600} gutterBottom>
        {t('runSource.heading', 'Where this run came from')}
      </Typography>
      <Box
        component="dl"
        sx={{
          display: 'grid',
          gridTemplateColumns: 'max-content 1fr',
          columnGap: 2,
          rowGap: 0.5,
          m: 0,
          '& dt': { color: 'text.secondary' },
          '& dd': { m: 0, wordBreak: 'break-all' },
        }}
      >
        {rows.map((row) => (
          <Box key={row.key} sx={{ display: 'contents' }}>
            <Typography component="dt" variant="body2">
              {row.term}
            </Typography>
            <Typography component="dd" variant="body2">
              {row.value}
            </Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
}

/**
 * The same, in one line for the run history table: the short commit and the
 * branch, or the pull request. Plain text: the row already links to its run,
 * where the full details and links are.
 */
export function RunSourceSummary({ source }: RunSourceProps) {
  const { t } = useTranslation();
  if (!source) return null;
  const parts = [
    source.commitSha ? shortSha(source.commitSha) : null,
    source.pullRequest
      ? t('runSource.pullRequestShort', {
          defaultValue: 'PR #{{number}}',
          number: source.pullRequest.number,
        })
      : source.branch,
  ].filter((part): part is string => Boolean(part));
  if (parts.length === 0) return null;
  return (
    <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 0.5 }}>
      {parts.join(' · ')}
    </Typography>
  );
}
